import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications as notificationsApi } from '../services/api';

function NotificationsPage() {
  const navigate = useNavigate();
  const [notificationList, setNotificationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    loadNotifications(true);
  }, [filter]);

  const loadNotifications = async (reset = false) => {
    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;
      const { data } = await notificationsApi.getAll({
        page: currentPage,
        limit: 20,
        unread: filter === 'unread' ? true : undefined
      });

      if (reset) {
        setNotificationList(data);
        setPage(1);
      } else {
        setNotificationList(prev => [...prev, ...data]);
      }

      setHasMore(data.length === 20);
    } catch (err) {
      setError('Error carregant les notificacions');
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotificationList(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotificationList(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsApi.delete(id);
      setNotificationList(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.data?.assessmentId) {
      navigate(`/assessments/${notification.data.assessmentId}`);
    } else if (notification.data?.caseId) {
      navigate(`/cases/${notification.data.caseId}`);
    } else if (notification.data?.projectId) {
      navigate(`/projects/${notification.data.projectId}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'assessment_graded':
        return (
          <div className="w-10 h-10 rounded-full bg-success-subtle flex items-center justify-center">
            <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'assessment_submitted':
        return (
          <div className="w-10 h-10 rounded-full bg-accent-subtle flex items-center justify-center">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
        );
      case 'team_invitation':
        return (
          <div className="w-10 h-10 rounded-full bg-info-subtle flex items-center justify-center">
            <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        );
      case 'project_published':
        return (
          <div className="w-10 h-10 rounded-full bg-warning-subtle flex items-center justify-center">
            <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center">
            <svg className="w-5 h-5 text-content-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString('ca-ES', { year: 'numeric', month: 'short', day: 'numeric' });
    } else if (days > 0) {
      return `Fa ${days} ${days === 1 ? 'dia' : 'dies'}`;
    } else if (hours > 0) {
      return `Fa ${hours} ${hours === 1 ? 'hora' : 'hores'}`;
    } else if (minutes > 0) {
      return `Fa ${minutes} ${minutes === 1 ? 'minut' : 'minuts'}`;
    } else {
      return 'Ara mateix';
    }
  };

  const unreadCount = notificationList.filter(n => !n.isRead).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Notificacions</h1>
          <p className="text-content-secondary">
            {unreadCount > 0 ? `${unreadCount} sense llegir` : 'Totes les notificacions llegides'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="btn btn-secondary text-sm"
          >
            Marcar totes com llegides
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-accent-subtle text-accent-hover'
              : 'bg-surface-muted text-content-secondary hover:bg-surface-muted'
          }`}
        >
          Totes
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'unread'
              ? 'bg-accent-subtle text-accent-hover'
              : 'bg-surface-muted text-content-secondary hover:bg-surface-muted'
          }`}
        >
          Sense llegir
        </button>
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading && notificationList.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : notificationList.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="w-16 h-16 text-content-disabled mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <h3 className="text-lg font-medium text-content mb-2">Cap notificació</h3>
          <p className="text-content-muted">
            {filter === 'unread' ? 'No tens notificacions sense llegir.' : 'No tens cap notificació.'}
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-border-subtle">
          {notificationList.map((notification) => (
            <div
              key={notification.id}
              className={`flex items-start p-4 hover:bg-surface-subtle transition-colors ${!notification.isRead ? 'bg-accent-subtle' : ''}`}
            >
              <button
                onClick={() => handleNotificationClick(notification)}
                className="flex items-start flex-1 text-left"
              >
                {getNotificationIcon(notification.type)}
                <div className="ml-4 flex-1 min-w-0">
                  <p className={`text-sm ${!notification.isRead ? 'font-semibold' : ''} text-content`}>
                    {notification.title}
                  </p>
                  <p className="text-sm text-content-secondary mt-1">{notification.message}</p>
                  <p className="text-xs text-content-disabled mt-2">{formatDate(notification.createdAt)}</p>
                </div>
              </button>
              <div className="flex items-center space-x-2 ml-4">
                {!notification.isRead && (
                  <button
                    onClick={() => handleMarkAsRead(notification.id)}
                    className="p-1 text-content-disabled hover:text-accent"
                    title="Marcar com llegida"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => handleDelete(notification.id)}
                  className="p-1 text-content-disabled hover:text-error"
                  title="Eliminar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && notificationList.length > 0 && (
        <div className="text-center">
          <button
            onClick={() => {
              setPage(p => p + 1);
              loadNotifications(false);
            }}
            disabled={loading}
            className="btn btn-secondary disabled:opacity-50"
          >
            {loading ? 'Carregant...' : 'Carregar més'}
          </button>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
